'use client';
import { type PropsWithChildren } from 'react';

import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import { enqueueSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { t } from '@/utils/lang';

interface HoverCopyCellProps extends PropsWithChildren {

    /**
     * Value to copy
     **/
    value?: string | number;
    children?: React.ReactNode;
}

export default function HoverCopyCell({ value, children }: HoverCopyCellProps) {
    const text = String(value ?? '');

    const onCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        try {
            await navigator.clipboard.writeText(text);

            enqueueSnackbar(t('Đã sao chép'), {
                variant: 'success',
                autoHideDuration: 1000,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        } catch {}
    };

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <div
                className='text-ellipsis overflow-hidden whitespace-nowrap w-full'
                title={text}
            >
                {children || text}
            </div>

            <Box
                className='hover-actions'
                sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    transition: t =>
                        t.transitions.create('opacity', { duration: 120 }),
                }}
            >
                <Tooltip title={t('Sao chép')}>
                    <IconButton
                        size='small'
                        aria-label='Copy value'
                        onClick={onCopy}
                        onMouseDown={e => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                        tabIndex={-1}
                        data-stop-row-click
                    >
                        <ContentCopyRounded fontSize='inherit' />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}
